import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { collection, getDocs, doc, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import '../../styles/ClientDashboard.css'; // Assuming you'll use the same styles

const DietTemplateManager = () => {
  // Template Form State
  const [templateName, setTemplateName] = useState('');
  const [mealsGrid, setMealsGrid] = useState(Array(7).fill(null).map(() => Array(8).fill('')));
  const [notes, setNotes] = useState('');
  const [mealTimings, setMealTimings] = useState(Array(8).fill(''));
  const [editingTemplate, setEditingTemplate] = useState(null); // Stores the template being edited (or null)

  // Data and Status States
  const [globalTemplates, setGlobalTemplates] = useState([]);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);

  // --- Data Fetching Logic ---

  // Fetch all global diet templates
  const fetchGlobalTemplates = async () => {
    try {
      const templatesCollection = collection(db, 'globalDietTemplates');
      const querySnapshot = await getDocs(templatesCollection);
      const fetchedTemplates = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        // Ensure weeklyDietPlan and mealTimings are structured correctly for editing
        weeklyDietPlan: doc.data().weeklyDietPlan || Array(7).fill(null).map(() => ({ meals: Array(8).fill('') })),
        mealTimings: doc.data().mealTimings || Array(8).fill(''),
      }));
      setGlobalTemplates(fetchedTemplates);
    } catch (err) {
      console.error('Error fetching global templates:', err);
      setError('Failed to load templates.');
    }
  };

  useEffect(() => {
    fetchGlobalTemplates();
  }, []);

  // --- Form Manipulation Functions ---

  const resetForm = () => {
    setTemplateName('');
    setMealsGrid(Array(7).fill(null).map(() => Array(8).fill('')));
    setNotes('');
    setMealTimings(Array(8).fill(''));
    setEditingTemplate(null);
    setStatus(null);
    setError(null);
  };

  const handleMealChange = (row, col, value) => {
    const newMealsGrid = [...mealsGrid];
    newMealsGrid[row][col] = value;
    setMealsGrid(newMealsGrid);
  };

  const handleMealTimingChange = (index, value) => {
    const newMealTimings = [...mealTimings];
    newMealTimings[index] = value;
    setMealTimings(newMealTimings);
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateName(template.name);
    // Reconstruct mealsGrid from the saved weeklyDietPlan structure
    const grid = template.weeklyDietPlan.map(dayPlan => dayPlan.meals.map(meal => `${meal}`));
    setMealsGrid(grid);
    setNotes(template.notes || '');
    setMealTimings(template.mealTimings || Array(8).fill(''));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCopyTemplate = (template) => {
    resetForm();
    setTemplateName(`Copy of ${template.name}`);
    // Reconstruct mealsGrid for copy operation
    const grid = template.weeklyDietPlan.map(dayPlan => [...dayPlan.meals]);
    setMealsGrid(grid);
    setNotes(template.notes || '');
    setMealTimings([...template.mealTimings] || Array(8).fill(''));
    setStatus(`Template copied to form. Save to create a new template.`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // --- CRUD Operations ---

  const handleSaveTemplate = async (e) => {
    e.preventDefault();
    setStatus('Saving template...');
    setError(null);

    if (!templateName.trim()) {
      setError('Template name is required.');
      setStatus(null);
      return;
    }

    try {
      // Prepare data in the expected structure
      const weeklyDietPlan = Array(7).fill(null).map((_, dayIndex) => {
        // Dates are irrelevant for a template, but maintaining the structure is safest
        return { date: `Day ${dayIndex + 1}`, meals: mealsGrid[dayIndex] };
      });

      const templateData = {
        name: templateName,
        weeklyDietPlan: weeklyDietPlan,
        notes: notes,
        mealTimings: mealTimings,
        lastModified: serverTimestamp(),
      };

      const templateId = editingTemplate ? editingTemplate.id : doc(collection(db, 'globalDietTemplates')).id;
      const templateDocRef = doc(db, 'globalDietTemplates', templateId);

      await setDoc(templateDocRef, templateData, { merge: true });

      setStatus(`Template "${templateName}" ${editingTemplate ? 'updated' : 'created'} successfully!`);
      resetForm();
      fetchGlobalTemplates(); // Refresh the list
      setTimeout(() => setStatus(null), 3000);

    } catch (err) {
      console.error('Error saving template:', err);
      setError(`Failed to save template. Error: ${err.message}`);
      setStatus(null);
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleDeleteTemplate = async (templateId, templateName) => {
    if (window.confirm(`Are you sure you want to delete the template: "${templateName}"? This cannot be undone.`)) {
      try {
        const templateDocRef = doc(db, 'globalDietTemplates', templateId);
        await deleteDoc(templateDocRef);
        setStatus(`Template "${templateName}" deleted successfully!`);
        fetchGlobalTemplates(); // Refresh the list
        if (editingTemplate?.id === templateId) {
          resetForm();
        }
        setTimeout(() => setStatus(null), 3000);
      } catch (err) {
        console.error('Error deleting template:', err);
        setError('Failed to delete template.');
        setTimeout(() => setError(null), 3000);
      }
    }
  };

  // --- Component Render ---

  return (
    <div className="dashboard-container client-dashboard">
      <h1>Diet Template Manager</h1>
      <p>Create, save, and manage reusable diet plan templates. These can be quickly applied to any customer's plan.</p>

      <div className="dashboard-content">
        <h2>{editingTemplate ? `Edit Template: ${editingTemplate.name}` : 'Create New Template'}</h2>
        <form onSubmit={handleSaveTemplate} className="diet-plan-form">
          <div>
            <label htmlFor="templateName">Template Name:</label>
            <input
              type="text"
              id="templateName"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              required
            />
          </div>

          {/* Weekly meals grid - Simplified for template (no dates needed) */}
          <div className="weekly-meals-grid">
            <h3>Template Meal Plan (7 Days)</h3>
            <table className="meals-table">
              <thead>
                <tr>
                  <th>Day</th>
                  {Array.from({ length: 8 }, (_, index) => (
                    <th key={index}>
                      <input
                        type="text"
                        placeholder={`Meal ${index + 1} Timing`}
                        value={mealTimings[index]}
                        onChange={(e) => handleMealTimingChange(index, e.target.value)}
                        className="timing-input"
                      />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Array(7).fill(null).map((_, rowIndex) => (
                  <tr key={rowIndex}>
                    <th>{`Day ${rowIndex + 1}`}</th>
                    {Array.from({ length: 8 }, (_, colIndex) => (
                      <td key={colIndex}>
                        <input
                          type="text"
                          placeholder=""
                          value={mealsGrid[rowIndex][colIndex]}
                          onChange={(e) => handleMealChange(rowIndex, colIndex, e.target.value)}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div>
            <label htmlFor="notes">Template Notes/Description:</label>
            <textarea id="notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows="4" />
          </div>

          <div className="form-actions">
            <button type="submit">
              {editingTemplate ? 'Update Template' : 'Save New Template'}
            </button>
            <button type="button" onClick={resetForm} className="secondary-button">
              Clear Form
            </button>
          </div>

          {status && <p className="upload-status">{status}</p>}
          {error && <p className="upload-error">{error}</p>}
        </form>

        <hr style={{ margin: '20px 0' }} />

        {/* Template List */}
        <div className="existing-templates">
          <h3>Existing Global Templates ({globalTemplates.length})</h3>
          {globalTemplates.length === 0 ? (
            <p>No templates created yet.</p>
          ) : (
            <ul>
              {globalTemplates.map((template) => (
                <li key={template.id}>
                  <strong>{template.name}</strong>
                  <span className="list-meta">
                    (Meals: {template.mealTimings.filter(t => t).length})
                  </span>
                  <button onClick={() => handleEditTemplate(template)}>Edit</button>
                  <button onClick={() => handleCopyTemplate(template)}>Copy</button>
                  <button onClick={() => handleDeleteTemplate(template.id, template.name)} className="delete-button">Delete</button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default DietTemplateManager;
